import axios from 'axios';

const api = axios.create({ baseURL: '' });

let _token: string | null = localStorage.getItem('tg_token');

export const setToken = (token: string | null) => {
  _token = token;
  if (token) localStorage.setItem('tg_token', token);
  else localStorage.removeItem('tg_token');
};

api.interceptors.request.use(config => {
  if (_token) config.headers['Authorization'] = `Bearer ${_token}`;
  return config;
});

export const auth = {
  login: async (username: string, password: string) => { const res = await api.post('/api/auth/login', { username, password }); setToken(res.data.token); return res; },
  logout: () => { setToken(null); return api.post('/api/auth/logout'); },
  me: () => api.get('/api/auth/me'),
  changePassword: (current_password: string, new_password: string) => api.post('/api/auth/change-password', { current_password, new_password }),
  changeUsername: (new_username: string, password: string) => api.post('/api/auth/change-username', { new_username, password }),
  getUsers: () => api.get('/api/auth/users'),
  createUser: (data: { username: string; password: string; role: string; permissions?: string[]; display_name?: string }) => api.post('/api/auth/users', data),
  updateUser: (id: number, data: { role?: string; permissions?: string[]; display_name?: string; is_active?: boolean; new_password?: string }) => api.put(`/api/auth/users/${id}`, data),
  deleteUser: (id: number) => api.delete(`/api/auth/users/${id}`),
  getAvailablePages: () => api.get('/api/auth/users/available-pages'),
  getActivityLog: (limit?: number) => api.get('/api/auth/activity-log', { params: { limit } }),
};
export const inventory = {
  getAll: () => api.get('/api/inventory/'),
  create: (data: any) => api.post('/api/inventory/', data),
  createBulk: (items: any[]) => api.post('/api/inventory/bulk', items),
  update: (id: number, data: any) => api.put(`/api/inventory/${id}`, data),
  delete: (id: number) => api.delete(`/api/inventory/${id}`),
  addPurchase: (data: any) => api.post('/api/inventory/purchase', data),
  getLowStock: () => api.get('/api/inventory/low-stock'),
  getCategories: () => api.get('/api/inventory/categories'),
  createCategory: (data: any) => api.post('/api/inventory/categories', data),
  updateCategory: (id: number, data: any) => api.put(`/api/inventory/categories/${id}`, data),
  deleteCategory: (id: number) => api.delete(`/api/inventory/categories/${id}`),
  getAnalytics: () => api.get('/api/inventory/analytics'),
  getItemSizes: (article_type: string, color: string) => api.get('/api/inventory/item-sizes', { params: { article_type, color } }),
  getReorderSuggestions: () => api.get('/api/inventory/reorder-suggestions'),
  bulkCategorize: (item_ids: number[], category_id: number | null) => api.post('/api/inventory/bulk-categorize', { item_ids, category_id }),
  updateGroupMeta: (data: { old_article_type: string; old_color: string; new_article_type?: string; new_color?: string; category_id?: number | null }) => api.put('/api/inventory/group-meta', data),
  updateGroupPrice: (data: { article_type: string; color: string; cost_price: number }) => api.put('/api/inventory/group-price', data),
  mergeItems: (data: { canonical_article_type: string; canonical_color: string; canonical_category_id?: number | null; source_item_ids: number[] }) => api.post('/api/inventory/merge', data),
  reorderCategories: (ordered_ids: number[]) => api.put('/api/inventory/categories/reorder', { ordered_ids }),
};
export const rto = {
  getAll: (status?: string) => api.get('/api/rto/', { params: { status } }),
  addManual: (data: any) => api.post('/api/rto/add', data),
  suggest: (rtoId: number) => api.get(`/api/rto/suggest/${rtoId}`),
  assign: (rtoId: number, orderName: string) => api.post(`/api/rto/assign/${rtoId}`, { order_name: orderName }),
  unassign: (rtoId: number) => api.post(`/api/rto/unassign/${rtoId}`),
  update: (id: number, data: any) => api.put(`/api/rto/${id}`, data),
  getStats: () => api.get('/api/rto/stats'),
  getHistory: (month?: string) => api.get('/api/rto/history', { params: { month } }),
  getDesignsByDate: (month?: string) => api.get('/api/rto/designs-by-date', { params: { month } }),
  downloadTemplate: () => api.get('/api/rto/template', { responseType: 'blob' }),
  scanLookup: (scanText: string) => api.post('/api/rto/scan/lookup', { scan_text: scanText }),
  delete: (id: number) => api.delete(`/api/rto/${id}`),
  deleteAll: () => api.delete('/api/rto/all'),
  exportAssigned: (startDate?: string, endDate?: string) => api.get('/api/rto/export/assigned', { params: { start_date: startDate, end_date: endDate }, responseType: 'blob' }),
  scanConfirm: (scanText: string, selectedItems: { article_type: string; color: string; size: string }[]) =>
    api.post('/api/rto/scan/confirm', { scan_text: scanText, selected_items: selectedItems }),
  suggestAll: () => api.get('/api/rto/suggest-all'),
  ignoreOrder: (orderName: string) => api.post('/api/rto/ignore-order', { order_name: orderName }),
  unignoreOrder: (orderName: string) => api.post('/api/rto/unignore-order', { order_name: orderName }),
  getIgnoredOrders: () => api.get('/api/rto/ignored-orders'),
  scanOCR: (imageBase64: string) => api.post('/api/scan/ocr', { image_base64: imageBase64 }),
  scanOCRConfirm: (orderName: string) => api.post('/api/scan/ocr/confirm', { order_name: orderName }),
  assignAll: () => api.post('/api/rto/assign-all'),
  exportMatched: () => api.get('/api/rto/export/matched', { responseType: 'blob' }),
  matchUpload: (file: File, courier: 'delhivery' | 'nimbuspost' = 'delhivery') => { const form = new FormData(); form.append('file', file); form.append('courier', courier); return api.post('/api/rto/match-upload', form); },
  printMatchList: (rows: any[]) => api.post('/api/rto/match-upload/pdf', { rows }, { responseType: 'blob' }),
  printEmbroideryNeeded: (rows: any[]) => api.post('/api/rto/match-upload/pdf-embroidery-needed', { rows }, { responseType: 'blob' }),
  publishChecklist: (rows: any[], matchedCount: number, total: number) =>
    api.post('/api/rto/checklist-session', { rows, matched_count: matchedCount, total }),
  getLatestChecklist: () => api.get('/api/rto/checklist-session/latest'),
  clearChecklist: () => api.delete('/api/rto/checklist-session/latest'),
  setChecklistRowStatus: (rowIndex: number, status: 'collected' | 'skipped' | null) =>
    api.post('/api/rto/checklist-session/latest/row-status', { row_index: rowIndex, status }),
  // One upload, one backend scan — returns all 5 variants (combined + 3 tiers +
  // embroidery-needed) base64-encoded in one JSON body, instead of a separate
  // upload+scan per tier.
  reorderLabels: (files: File[]) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    return api.post<{ all: string; '1': string; '2': string; '3': string; embroidery: string }>('/api/rto/match-upload/reorder-labels', form);
  },
  getReorderTierCounts: () => api.get('/api/rto/match-upload/reorder-labels/tier-counts'),
};
export const shopify = {
  sync: () => api.post('/api/shopify/sync'),
  getOrders: (params?: any) => api.get('/api/shopify/orders', { params }),
  getDashboardStats: (target_date?: string) => api.get('/api/shopify/dashboard-stats', { params: { target_date } }),
  getSyncStatus: () => api.get('/api/sync-status'),
  updateOrderStatus: (orderId: number, status: string) => api.post(`/api/shopify/orders/${orderId}/status`, { status }),
  getDispatchStats: (date?: string) => api.get('/api/shopify/dispatch-stats', { params: { date } }),
  deleteOrder: (orderId: number) => api.delete(`/api/shopify/orders/${orderId}`),
  getTransitKpi: (params?: { start_date?: string; end_date?: string }) => api.get('/api/shopify/transit-kpi', { params }),
};
export const analytics = {
  summary: (start_date?: string, end_date?: string) => api.get('/api/analytics/summary', { params: { start_date, end_date } }),
  revenueTrend: (start_date?: string, end_date?: string) => api.get('/api/analytics/revenue-trend', { params: { start_date, end_date } }),
  topProducts: (start_date?: string, end_date?: string, limit?: number) => api.get('/api/analytics/top-products', { params: { start_date, end_date, limit } }),
  topVariants: (start_date?: string, end_date?: string, limit?: number) => api.get('/api/analytics/top-variants', { params: { start_date, end_date, limit } }),
  stateWise: (start_date?: string, end_date?: string) => api.get('/api/analytics/state-wise', { params: { start_date, end_date } }),
  topSelling: (start_date?: string, end_date?: string) => api.get('/api/analytics/top-selling', { params: { start_date, end_date } }),
  topSizes: (start_date?: string, end_date?: string) => api.get('/api/analytics/top-sizes', { params: { start_date, end_date } }),
  productSizes: (product_title: string, start_date?: string, end_date?: string) => api.get('/api/analytics/product-sizes', { params: { product_title, start_date, end_date } }),
  rtoRate: (start_date?: string, end_date?: string) => api.get('/api/analytics/rto-rate', { params: { start_date, end_date } }),
};
export const expenses = {
  getAll: (month?: number, year?: number, from_date?: string, to_date?: string) => api.get('/api/expenses/', { params: { month, year, from_date, to_date } }),
  getAnomalies: (year: number, month: number) => api.get('/api/expenses/anomalies', { params: { year, month } }),
  create: (data: any) => api.post('/api/expenses/', data),
  delete: (id: number) => api.delete(`/api/expenses/${id}`),
  getCategories: () => api.get('/api/expenses/categories'),
  createCategory: (data: any) => api.post('/api/expenses/categories', data),
  deleteCategory: (id: number) => api.delete(`/api/expenses/categories/${id}`),
  updateCategory: (id: number, data: { gst_rate?: number; exclude_from_pl?: boolean }) => api.patch(`/api/expenses/categories/${id}`, data),
  update: (id: number, data: Partial<{ category: string; expense_type: string; amount: number; date: string; notes: string }>) => api.patch(`/api/expenses/${id}`, data),
  importStatement: (file: File) => { const form = new FormData(); form.append('file', file); return api.post('/api/expenses/import-statement', form); },
  importBulk: (rows: { date: string; narration: string; amount: number; category: string; expense_type: string }[]) => api.post('/api/expenses/import-bulk', { rows }),
  scanInvoice: (file: File) => { const form = new FormData(); form.append('file', file); return api.post('/api/expenses/scan-invoice', form); },
  saveInvoice: (file: File, fields: { supplier: string; invoice_date: string; base_amount: number; gst_amount: number; total_amount: number; category: string; expense_type: string; invoice_number: string }) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(fields).forEach(([k, v]) => form.append(k, String(v)));
    return api.post('/api/expenses/save-invoice', form);
  },
};
export const reconciliation = {
  blankMaterials: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/reconciliation/blank-materials', form);
  },
  plExcel: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/reconciliation/pl-excel', form);
  },
};
export const cashflow = {
  import: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/cashflow/import', form);
  },
  getMonthly: () => api.get('/api/cashflow/monthly'),
  getTransactions: (year: number, month: number) =>
    api.get('/api/cashflow/transactions', { params: { year, month } }),
  updateTransaction: (id: number, fields: Partial<{
    date: string; narration: string; debit_amount: number;
    credit_amount: number; balance: number; classification: string;
  }>) => api.patch(`/api/cashflow/transactions/${id}`, fields),
  setClassificationBulk: (ids: number[], classification: string) =>
    api.patch('/api/cashflow/transactions/bulk-classify', { ids, classification }),
  deleteTransaction: (id: number) => api.delete(`/api/cashflow/transactions/${id}`),
  deleteTransactionsBulk: (ids: number[]) =>
    api.post('/api/cashflow/transactions/bulk-delete', { ids }),
};
export const pl = {
  get: (year: number, month: number, provisional = false) => api.get(`/api/pl/${year}/${month}${provisional ? '?provisional=true' : ''}`),
  getProducts: (year: number, month: number) => api.get(`/api/pl/${year}/${month}/products`),
  getRange: (fy: number, fm: number, ty: number, tm: number, provisional = false) =>
    api.get(`/api/pl/range?from_year=${fy}&from_month=${fm}&to_year=${ty}&to_month=${tm}${provisional ? '&provisional=true' : ''}`),
  getRangeProducts: (fy: number, fm: number, ty: number, tm: number) =>
    api.get(`/api/pl/range/products?from_year=${fy}&from_month=${fm}&to_year=${ty}&to_month=${tm}`),
  compare: (ay: number, am: number, by: number, bm: number, provisional = false) =>
    api.get(`/api/pl/compare?year_a=${ay}&month_a=${am}&year_b=${by}&month_b=${bm}${provisional ? '&provisional=true' : ''}`),
  rematchInventory: () => api.post('/api/pl/rematch-inventory'),
  lockStatus: () => api.get('/api/pl/lock-status'),
  unlock: (pin: string) => api.post('/api/pl/unlock', { pin }),
  lock: () => api.post('/api/pl/lock'),
  setPin: (pin: string, adminPassword: string) => api.post('/api/pl/set-pin', { pin, admin_password: adminPassword }),
};
export const tax = {
  export: (year: number, month: number) => api.get(`/api/tax/export/${year}/${month}`, { responseType: 'blob' }),
  salesByState: (year: number, month: number) => api.get(`/api/tax/sales-by-state/${year}/${month}`),
};
export const settings = {
  getAll: () => api.get('/api/settings/'),
  update: (key: string, value: string) => api.post('/api/settings/', { key, value }),
  uploadLogo: (file: File, mode: 'dark' | 'light' = 'dark') => { const form = new FormData(); form.append('file', file); return api.post(`/api/settings/logo?mode=${mode}`, form); },
  deleteLogo: (mode: string = 'dark') => api.delete(`/api/settings/logo?mode=${mode}`),
};
export const delhivery = {
  track: (orderId: number) => api.get(`/api/delhivery/track/${orderId}`),
  trackLive: (awbCode: string) => api.get(`/api/delhivery/track-live/${awbCode}`),
  trackLiveOrder: (orderId: number) => api.get(`/api/delhivery/track-live-order/${orderId}`),
};
export const search = { global: (q: string) => api.get('/api/search', { params: { q } }), notifications: () => api.get('/api/notifications') };
export const invoices = {
  getAll: (month?: number, year?: number) => api.get('/api/invoices/', { params: { month, year } }),
  upload: (file: File, dealer_name: string, date: string, amount: number, notes: string, dealer_id?: number, base_amount?: number, gst_amount?: number, invoice_number?: string) => {
    const form = new FormData(); form.append('file', file); form.append('dealer_name', dealer_name); form.append('date', date); form.append('amount', amount.toString()); form.append('notes', notes);
    if (dealer_id) form.append('dealer_id', dealer_id.toString());
    if (base_amount != null) form.append('base_amount', base_amount.toString());
    if (gst_amount != null) form.append('gst_amount', gst_amount.toString());
    if (invoice_number) form.append('invoice_number', invoice_number);
    return api.post('/api/invoices/upload', form);
  },
  extractAmount: (file: File) => { const form = new FormData(); form.append('file', file); return api.post('/api/invoices/extract-amount', form); },
  download: (id: number) => api.get(`/api/invoices/download/${id}`, { responseType: 'blob' }),
  bulkDownload: (ids: number[]) => api.post('/api/invoices/bulk-download', { invoice_ids: ids }, { responseType: 'blob' }),
  delete: (id: number) => api.delete(`/api/invoices/${id}`),
  getDealers: () => api.get('/api/invoices/dealers'),
  createDealer: (data: any) => api.post('/api/invoices/dealers', data),
  deleteDealer: (id: number) => api.delete(`/api/invoices/dealers/${id}`),
  saveItems: (id: number, items: any[]) => api.post(`/api/invoices/${id}/items`, { items }),
};
export const proforma = {
  list: (archived = false) => api.get('/api/proforma/', { params: { archived } }),
  generate: (data: any) => api.post('/api/proforma/generate', data, { responseType: 'blob' }),
  download: (id: number) => api.get(`/api/proforma/download/${id}`, { responseType: 'blob' }),
  delete: (id: number) => api.delete(`/api/proforma/${id}`),
  createReceive: (data: any) => api.post('/api/proforma/receives', data),
  listReceives: () => api.get('/api/proforma/receives'),
  addToStock: (receiveId: number) => api.post(`/api/proforma/receives/${receiveId}/add-to-stock`),
  saveTracking: (id: number, tracking_number: string) => api.patch(`/api/proforma/${id}/tracking`, { tracking_number }),
  regenerate: (id: number) => api.post(`/api/proforma/regenerate/${id}`, {}, { responseType: 'blob' }),
  getTrackings: (id: number) => api.get(`/api/proforma/${id}/trackings`),
  addTracking: (id: number, awb: string, note?: string) => api.post(`/api/proforma/${id}/trackings`, { awb, note }),
  updateTracking: (id: number, trackingId: number, awb: string, note?: string) => api.patch(`/api/proforma/${id}/trackings/${trackingId}`, { awb, note }),
  deleteTracking: (id: number, trackingId: number) => api.delete(`/api/proforma/${id}/trackings/${trackingId}`),
  archive: (id: number) => api.patch(`/api/proforma/${id}/archive`),
};
export const designMappings = {
  getAll: () => api.get('/api/design-mappings/'),
  create: (data: { shopify_title: string; blank_article: string; blank_color: string; notes?: string }) =>
    api.post('/api/design-mappings/', data),
  createBulk: (mappings: { shopify_title: string; blank_article: string; blank_color: string; notes?: string }[]) =>
    api.post('/api/design-mappings/bulk', { mappings }),
  update: (id: number, data: { shopify_title?: string; blank_article?: string; blank_color?: string; notes?: string }) =>
    api.put(`/api/design-mappings/${id}`, data),
  delete: (id: number) => api.delete(`/api/design-mappings/${id}`),
  getInventoryBlanks: () => api.get('/api/design-mappings/inventory-blanks'),
  getShopifyProducts: () => api.get('/api/design-mappings/shopify-products'),
  getUnmapped: () => api.get('/api/design-mappings/unmapped-products'),
  getUnmatchedLineItems: () => api.get('/api/design-mappings/unmatched-line-items'),
  dismissUnmatched: (product_title: string) => api.post('/api/design-mappings/unmatched-line-items/dismiss', { product_title }),
};
export const maintenance = {
  getReorder: (days?: number, threshold?: number) => api.get('/api/maintenance/reorder', { params: { days, threshold_days: threshold } }),
  getDbStats: () => api.get('/api/maintenance/db-stats'),
  runCleanup: () => api.post('/api/maintenance/cleanup'),
  cleanupCancelled: (days?: number) => api.post('/api/maintenance/cleanup/cancelled-orders', null, { params: { days } }),
};
export const dispatch = {
  exportReport: (date: string) => api.get("/api/dispatch/report", { params: { date }, responseType: "blob" }),
};
export const customerRisk = {
  list: (all: boolean = false) => api.get('/api/customer-risk/', { params: { all } }),
  getOrders: (customerKey: string) => api.get(`/api/customer-risk/${encodeURIComponent(customerKey)}`),
  getDaily: (date?: string) => api.get('/api/customer-risk/daily', { params: { date } }),
};
export default api;

export const borrowed = {
  getAll: () => api.get('/api/borrowed/'),
  add: (data: any) => api.post('/api/borrowed/', data),
  addBulk: (data: any) => api.post('/api/borrowed/bulk', data),
  update: (id: number, data: any) => api.put(`/api/borrowed/${id}`, data),
  delete: (id: number) => api.delete(`/api/borrowed/${id}`),
  returnStock: (id: number, data: { quantity: number; notes?: string }) => api.post(`/api/borrowed/return/${id}`, data),
  deductStock: (id: number, data: { quantity: number; notes?: string }) => api.post(`/api/borrowed/deduct/${id}`, data),
  getSettlement: () => api.get('/api/borrowed/settlement'),
  getLogs: (limit?: number) => api.get('/api/borrowed/logs', { params: { limit } }),
};

export const aiChat = {
  ask: (question: string) => api.post('/api/ai/chat', { question }),
};

export const vault = {
  pinStatus: () => api.get('/api/vault/pin/status'),
  setPin: (pin: string) => api.post('/api/vault/pin/set', { pin }),
  resetPin: (login_password: string, new_pin: string) => api.post('/api/vault/pin/reset', { login_password, new_pin }),
  unlock: (pin: string) => api.post('/api/vault/unlock', { pin }),
  lock: () => api.post('/api/vault/lock'),

  getProfiles: () => api.get('/api/vault/profiles'),
  createProfile: (data: { name: string; description?: string; icon?: string }) => api.post('/api/vault/profiles', data),
  updateProfile: (id: number, data: { name?: string; description?: string; icon?: string }) => api.put(`/api/vault/profiles/${id}`, data),
  deleteProfile: (id: number, force = false) => api.delete(`/api/vault/profiles/${id}`, { params: { force } }),

  getAccounts: (profileId: number) => api.get(`/api/vault/profiles/${profileId}/accounts`),
  createAccount: (profileId: number, data: any) => api.post(`/api/vault/profiles/${profileId}/accounts`, data),
  getAccount: (id: number) => api.get(`/api/vault/accounts/${id}`),
  updateAccount: (id: number, data: any) => api.put(`/api/vault/accounts/${id}`, data),
  deleteAccount: (id: number) => api.delete(`/api/vault/accounts/${id}`),
  revealAccount: (id: number) => api.get(`/api/vault/accounts/${id}/reveal`),

  createCard: (accountId: number, data: any) => api.post(`/api/vault/accounts/${accountId}/cards`, data),
  updateCard: (id: number, data: any) => api.put(`/api/vault/cards/${id}`, data),
  deleteCard: (id: number) => api.delete(`/api/vault/cards/${id}`),
  revealCard: (id: number) => api.get(`/api/vault/cards/${id}/reveal`),

  updateNetBanking: (accountId: number, data: any) => api.put(`/api/vault/accounts/${accountId}/net-banking`, data),
  revealNetBanking: (accountId: number) => api.get(`/api/vault/accounts/${accountId}/net-banking/reveal`),

  updateOtherInfo: (accountId: number, data: any) => api.put(`/api/vault/accounts/${accountId}/other-info`, data),
  revealMobile: (accountId: number) => api.get(`/api/vault/accounts/${accountId}/other-info/reveal-mobile`),

  createCustomField: (accountId: number, data: { field_name: string; field_value?: string; is_sensitive?: boolean }) => api.post(`/api/vault/accounts/${accountId}/custom-fields`, data),
  updateCustomField: (id: number, data: any) => api.put(`/api/vault/custom-fields/${id}`, data),
  deleteCustomField: (id: number) => api.delete(`/api/vault/custom-fields/${id}`),
  revealCustomField: (id: number) => api.get(`/api/vault/custom-fields/${id}/reveal`),

  search: (q: string) => api.get('/api/vault/search', { params: { q } }),
};
export const scratchUploads = {
  list: () => api.get('/api/scratch-uploads'),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/scratch-uploads', form);
  },
  delete: (name: string) => api.delete(`/api/scratch-uploads/${encodeURIComponent(name)}`),
};
